/**
 * PreviewCard - Generated card preview with edit action
 *
 * Shows the full generated card with all fields.
 * Card is auto-saved by the parent — this component only displays and allows editing.
 */

import { useState, useCallback } from 'react'
import type { GeneratedCard } from '@shared/schemas'
import { PronunciationButton } from '@/components/PronunciationButton'

interface PreviewCardProps {
  card: GeneratedCard
  imageUrl?: string
  onEdit: (card: GeneratedCard) => void
  onDiscard?: () => void
}

export function PreviewCard({ card, imageUrl, onEdit, onDiscard }: PreviewCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedCard, setEditedCard] = useState<GeneratedCard>(card)

  const handleFieldChange = useCallback((field: keyof GeneratedCard, value: string) => {
    setEditedCard(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleSaveEdit = useCallback(() => {
    setIsEditing(false)
    onEdit(editedCard)
  }, [editedCard, onEdit])

  const handleCancelEdit = useCallback(() => {
    setEditedCard(card)
    setIsEditing(false)
  }, [card])

  if (isEditing) {
    return (
      <div className="bg-surface-card rounded-xl p-5 shadow-card border border-hairline">
        <h3 className="font-headline font-bold text-sm text-on-surface-variant mb-4 uppercase tracking-wide">
          Edit Card
        </h3>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Front</label>
            <input
              type="text"
              value={editedCard.front}
              onChange={e => handleFieldChange('front', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-soft border border-hairline rounded-lg text-on-surface text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Back</label>
            <input
              type="text"
              value={editedCard.back}
              onChange={e => handleFieldChange('back', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-soft border border-hairline rounded-lg text-on-surface text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Definition</label>
            <textarea
              value={editedCard.definition}
              onChange={e => handleFieldChange('definition', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-soft border border-hairline rounded-lg text-on-surface text-sm resize-none focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
              rows={2}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Example</label>
            <textarea
              value={editedCard.example}
              onChange={e => handleFieldChange('example', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-soft border border-hairline rounded-lg text-on-surface text-sm resize-none focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
              rows={2}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Part of Speech</label>
            <input
              type="text"
              value={editedCard.pos}
              onChange={e => handleFieldChange('pos', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-soft border border-hairline rounded-lg text-on-surface text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSaveEdit}
            className="flex-1 py-2 bg-primary text-on-primary rounded-full font-semibold text-sm"
          >
            Save Edit
          </button>
          <button
            onClick={handleCancelEdit}
            className="flex-1 py-2 bg-surface-soft border border-hairline text-on-surface rounded-full font-semibold text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-surface-card rounded-xl p-5 shadow-card border border-hairline">
      <h3 className="font-headline font-bold text-sm text-on-surface-variant mb-4 uppercase tracking-wide">
        Generated Card
      </h3>

      {/* Image */}
      {imageUrl && (
        <div className="mb-4 rounded-xl overflow-hidden">
          <img src={imageUrl} alt={card.front} className="w-full max-h-28 object-contain bg-surface-soft rounded-xl" />
        </div>
      )}

      {/* Front */}
      <div className="mb-3">
        <p className="font-headline font-bold text-2xl text-on-surface">{card.front}</p>
        {(card.phonetic_us || card.phonetic_uk) && (
          <div className="flex items-center gap-3 mt-1">
            {card.phonetic_us && (
              <span className="text-sm text-on-surface-variant flex items-center gap-1">
                US {card.phonetic_us}
                <PronunciationButton text={card.front} accent="us" />
              </span>
            )}
            {card.phonetic_uk && (
              <span className="text-sm text-on-surface-variant flex items-center gap-1">
                UK {card.phonetic_uk}
                <PronunciationButton text={card.front} accent="uk" />
              </span>
            )}
          </div>
        )}
      </div>

      {/* Back */}
      <div className="mb-3">
        <p className="text-xs font-bold text-on-surface-variant uppercase">Translation</p>
        <p className="text-lg font-semibold text-on-surface">{card.back}</p>
      </div>

      {/* Definition */}
      <div className="mb-3">
        <p className="text-xs font-bold text-on-surface-variant uppercase">Definition</p>
        <p className="text-sm text-on-surface">{card.definition}</p>
      </div>

      {/* Example */}
      <div className="mb-3">
        <p className="text-xs font-bold text-on-surface-variant uppercase">Example</p>
        <p className="text-sm text-on-surface italic">&ldquo;{card.example}&rdquo;</p>
      </div>

      {/* POS */}
      <div className="flex items-center gap-2 mb-4">
        <span className="px-3 py-1 bg-surface-soft rounded-full text-xs font-bold text-on-surface-variant">
          {card.pos}
        </span>
        <a
          href={`https://www.google.com/search?q=${encodeURIComponent(card.front)}&tbm=isch`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-colors"
          aria-label={`Search images for ${card.front}`}
        >
          <span className="material-symbols-outlined text-[16px]">image_search</span>
          Images
        </a>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => setIsEditing(true)}
          className="flex-1 py-2 bg-surface-soft border border-hairline text-on-surface rounded-full font-semibold text-sm"
        >
          Edit
        </button>
        {onDiscard && (
          <button
            onClick={onDiscard}
            className="flex-1 py-2 bg-error-container text-on-error-container rounded-full font-semibold text-sm"
          >
            Discard
          </button>
        )}
      </div>
    </div>
  )
}
